import * as pdfjsLib from 'pdfjs-dist/build/pdf';

// Use CDN worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

/**
 * Extract text from PDF preserving row structure
 * Handles PDFs where each character is a separate text item
 */
export async function extractPdfText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const allLines = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const items = textContent.items.filter(item => item.str.trim().length > 0 || item.str === ' ');

    if (!items.length) continue;

    // Group by Y position (same row)
    const rows = {};
    items.forEach(item => {
      const y = Math.round(item.transform[5]);
      if (!rows[y]) rows[y] = [];
      rows[y].push({
        text: item.str,
        x: item.transform[4],
        width: item.width || 0,
      });
    });

    // Sort rows top-to-bottom
    const sortedYs = Object.keys(rows).map(Number).sort((a, b) => b - a);

    sortedYs.forEach(y => {
      const rowItems = rows[y].sort((a, b) => a.x - b.x);

      // Smart joining: group characters that are close together into words
      // Use significant gaps (>8px) as field separators (tab)
      let line = '';
      let prevEnd = -999;

      rowItems.forEach(item => {
        const gap = item.x - prevEnd;
        if (prevEnd === -999) {
          line += item.text;
        } else if (gap > 8) {
          // Significant gap = field separator
          line += '\t' + item.text;
        } else if (gap > 2 && item.text !== ' ') {
          // Small gap = potential space between words
          line += ' ' + item.text;
        } else {
          // Adjacent = same word
          line += item.text;
        }
        prevEnd = item.x + (item.width || item.text.length * 4);
      });

      const trimmed = line.trim();
      if (trimmed && trimmed.length > 1) allLines.push(trimmed);
    });
  }

  return allLines;
}

/**
 * Detect report type
 */
export function detectReportType(lines) {
  const fullText = lines.join(' ').toLowerCase();
  // Philstocks Transactions: has time patterns with AM/PM and buyer/seller columns
  if (fullText.includes('transaction')) return 'TRANSACTIONS';
  const hasAmPm = lines.some(l => /\d{1,2}:\d{2}(:\d{2})?\s*(am|pm)/i.test(l));
  if (hasAmPm && (fullText.includes('buyer') || fullText.includes('seller'))) return 'TRANSACTIONS';
  if (fullText.includes('buyer') && fullText.includes('seller') && !hasAmPm) return 'BUYERS_SELLERS';
  if (hasAmPm) return 'TRANSACTIONS';
  return 'BUYERS_SELLERS';
}

/**
 * Detect stock symbol from PDF lines
 */
export function detectSymbol(lines) {
  const fullText = lines.join('\n');
  // Pattern: "ACEN CORPORATION" or "Stock: ACEN" or standalone ticker
  const patterns = [
    /(?:stock|symbol|ticker|code)[:\s]+([A-Z]{2,6})/i,
    /\b([A-Z]{2,6})\s+CORPORATION\b/i,
    /\b([A-Z]{2,6})\s+CORP\.?\b/i,
    /Transactions\s*\(\s*([A-Z]{2,6})\s*\)/i, // "Transactions (ACEN)"
    /\(([A-Z]{2,6})\)/,
  ];
  for (const pat of patterns) {
    const match = fullText.match(pat);
    if (match) return match[1].toUpperCase();
  }
  // Check for standalone uppercase word that looks like a ticker (2-5 chars, on its own line or prominent)
  for (const line of lines.slice(0, 10)) {
    const m = line.match(/^([A-Z]{2,5})$/);
    if (m) return m[1];
  }
  return '';
}

/**
 * Detect trading date from PDF lines
 */
export function detectDate(lines) {
  const fullText = lines.join(' ');

  // ISO: 2024-01-15
  const iso = fullText.match(/(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];

  // Short format with 2-digit year: 5/5/26, 12/31/25, 1/15/26
  const shortYear = fullText.match(/(\d{1,2})\/(\d{1,2})\/(\d{2})(?:\s*,|\s)/);
  if (shortYear) {
    const [, m, d, y] = shortYear;
    const year = parseInt(y) < 50 ? `20${y}` : `19${y}`;
    return `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Full format: M/D/YYYY
  const fullDate = fullText.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (fullDate) {
    const [, m, d, y] = fullDate;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }

  // Written: January 15, 2024 or May 5, 2026
  const written = fullText.match(/((?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},?\s+\d{4})/i);
  if (written) {
    try {
      const d = new Date(written[1]);
      if (!isNaN(d)) return d.toISOString().split('T')[0];
    } catch {}
  }

  return '';
}

/**
 * Parse Philstocks Transactions PDF
 * Handles: Time (AM/PM), Price, Volume, Buyer, Seller
 */
export function parseTransactions(lines, symbol, tradingDate) {
  const records = [];

  for (const line of lines) {
    const lower = line.toLowerCase().trim();
    if (!lower) continue;

    // Skip headers and metadata
    if (lower.includes('time') && lower.includes('price') && lower.includes('volume')) continue;
    if (lower.includes('transaction') || lower.includes('total') || lower.includes('grand')) continue;
    if (lower.startsWith('stock:') || lower.startsWith('date:')) continue;
    if (lower.includes('corporation') || lower.includes('philstocks')) continue;
    if (lower.includes('morning') || lower.includes('afternoon')) continue;
    if (/^\d+\s*\/\s*\d+$/.test(lower)) continue; // page numbers like "1/20"

    // Look for time pattern: HH:MM:SS AM/PM or HH:MM:SS (24h)
    const timeMatch = line.match(/(\d{1,2}:\d{2}(?::\d{2})?)\s*(AM|PM|am|pm)?/i);
    if (!timeMatch) continue;

    let time = timeMatch[1];
    const ampm = timeMatch[2];
    if (ampm) time = `${time} ${ampm.toUpperCase()}`;

    // Split line into parts (tab or multi-space separated)
    const parts = line.split(/\t+/).map(s => s.trim()).filter(Boolean);
    if (parts.length < 3) {
      // Try space-split for non-tab PDFs
      const spaceParts = line.split(/\s{2,}/).map(s => s.trim()).filter(Boolean);
      if (spaceParts.length >= 4) {
        return parseTransactionLine(spaceParts, time, symbol, tradingDate, records);
      }
      continue;
    }

    // Parse: first part has time, rest are price, volume, buyer, seller
    const afterTime = parts.slice(1);
    if (afterTime.length < 3) continue;

    // Identify numbers (price, volume) and strings (buyer, seller)
    const nums = [];
    const strs = [];
    afterTime.forEach(p => {
      const cleaned = p.replace(/[,\s]/g, '');
      if (/^\d+\.?\d*$/.test(cleaned)) {
        nums.push(parseFloat(cleaned));
      } else {
        strs.push(p);
      }
    });

    const price = nums[0] || 0;
    const volume = nums.length > 1 ? nums[1] : (nums[0] > 100 ? nums[0] : 0);
    // If first num is price-like (< 1000) and second is volume-like
    let finalPrice = price;
    let finalVol = volume;
    if (nums.length >= 2) {
      if (nums[0] < 1000 && nums[1] >= 100) {
        finalPrice = nums[0];
        finalVol = nums[1];
      } else if (nums[0] >= 1000) {
        finalVol = nums[0];
        finalPrice = nums.length > 1 ? nums[1] : 0;
      }
    }

    const buyer = strs[0] || '';
    const seller = strs.length > 1 ? strs[1] : '';

    if (finalPrice <= 0 && finalVol <= 0) continue;
    if (!buyer && !seller) continue;

    records.push({
      symbol,
      time,
      price: finalPrice,
      volume: finalVol,
      buyer: buyer.toLowerCase(),
      seller: seller.toLowerCase(),
      tradingDate,
    });
  }

  return records;
}

function parseTransactionLine(parts, time, symbol, tradingDate, records) {
  // Fallback for space-separated lines
  const nums = [];
  const strs = [];
  parts.slice(1).forEach(p => {
    const cleaned = p.replace(/[,\s]/g, '');
    if (/^\d+\.?\d*$/.test(cleaned)) nums.push(parseFloat(cleaned));
    else strs.push(p);
  });
  if (nums.length >= 2 && strs.length >= 2) {
    records.push({
      symbol, time,
      price: nums[0] < 1000 ? nums[0] : nums[1],
      volume: nums[0] >= 1000 ? nums[0] : nums[1],
      buyer: strs[0].toLowerCase(),
      seller: strs[1].toLowerCase(),
      tradingDate,
    });
  }
  return records;
}

/**
 * Parse Buyers & Sellers report
 */
export function parseBuyersSellers(lines, symbol, tradingDate) {
  const records = [];
  let currentSide = null;

  for (const line of lines) {
    const lower = line.toLowerCase().trim();
    if (!lower) continue;

    if (/^buyers?\s*$/i.test(lower) || (lower.includes('buyer') && !lower.includes('seller') && lower.length < 30)) {
      currentSide = 'BUYER'; continue;
    }
    if (/^sellers?\s*$/i.test(lower) || (lower.includes('seller') && !lower.includes('buyer') && lower.length < 30)) {
      currentSide = 'SELLER'; continue;
    }

    if (lower.includes('rank') && lower.includes('broker')) continue;
    if (lower.includes('volume') && lower.includes('average') && lower.includes('%')) continue;
    if (lower.includes('total') || lower.includes('grand') || lower.includes('summary')) continue;
    if (lower.startsWith('stock:') || lower.startsWith('date:')) continue;
    if (!currentSide) continue;

    const parts = line.split(/\t+/).map(s => s.trim()).filter(Boolean);
    if (parts.length < 2) continue;

    let brokerIdx = 0;
    if (/^\d+\.?$/.test(parts[0])) brokerIdx = 1;

    const broker = parts[brokerIdx];
    if (!broker || broker.length < 2 || /^[\d.,\s%]+$/.test(broker)) continue;

    const numParts = parts.slice(brokerIdx + 1);
    const numbers = numParts.map(s => parseNumber(s));

    const volume = numbers[0] || 0;
    const average = numbers.length > 1 ? numbers[1] : 0;
    const volumePercent = numbers.length > 2 ? numbers[2] : 0;

    if (volume <= 0 && average <= 0) continue;

    records.push({ symbol, broker, side: currentSide, volume, average, volumePercent, tradingDate });
  }

  return records;
}

/**
 * Convert Buyers & Sellers records to broker data format
 */
export function buyersSellersTobrokerData(records, symbol, tradingDate) {
  const brokerMap = {};
  records.forEach(r => {
    const key = r.broker;
    if (!brokerMap[key]) brokerMap[key] = { buyVol: 0, buyAmt: 0, buyAve: 0, sellVol: 0, sellAmt: 0, sellAve: 0 };
    if (r.side === 'BUYER') {
      brokerMap[key].buyVol += r.volume;
      brokerMap[key].buyAve = r.average;
      brokerMap[key].buyAmt += r.volume * r.average;
    } else {
      brokerMap[key].sellVol += r.volume;
      brokerMap[key].sellAve = r.average;
      brokerMap[key].sellAmt += r.volume * r.average;
    }
  });

  return Object.entries(brokerMap).map(([broker, data]) => ({
    date: tradingDate, symbol, broker,
    buyVol: data.buyVol, buyAmt: Math.round(data.buyAmt * 100) / 100, buyAve: data.buyAve,
    sellVol: data.sellVol, sellAmt: Math.round(data.sellAmt * 100) / 100, sellAve: data.sellAve,
    netAmt: Math.round((data.buyAmt - data.sellAmt) * 100) / 100,
    totalValue: Math.round((data.buyAmt + data.sellAmt) * 100) / 100,
    netShares: data.buyVol - data.sellVol,
  }));
}

/**
 * Convert Transactions data to broker flow data + price data
 * This allows the system to generate signals from just transactions
 */
export function transactionsToBrokerAndPrice(transactions) {
  if (!transactions.length) return { brokerRows: [], priceRows: [] };

  // Group by date+symbol
  const groups = {};
  transactions.forEach(tx => {
    const key = `${tx.tradingDate}_${tx.symbol}`;
    if (!groups[key]) groups[key] = { date: tx.tradingDate, symbol: tx.symbol, txs: [] };
    groups[key].txs.push(tx);
  });

  const brokerRows = [];
  const priceRows = [];

  Object.values(groups).forEach(({ date, symbol, txs }) => {
    // Aggregate broker flows
    const brokerMap = {};
    txs.forEach(tx => {
      // Buyer side
      if (tx.buyer) {
        if (!brokerMap[tx.buyer]) brokerMap[tx.buyer] = { buyVol: 0, sellVol: 0, buyAmt: 0, sellAmt: 0 };
        brokerMap[tx.buyer].buyVol += tx.volume;
        brokerMap[tx.buyer].buyAmt += tx.volume * tx.price;
      }
      // Seller side
      if (tx.seller) {
        if (!brokerMap[tx.seller]) brokerMap[tx.seller] = { buyVol: 0, sellVol: 0, buyAmt: 0, sellAmt: 0 };
        brokerMap[tx.seller].sellVol += tx.volume;
        brokerMap[tx.seller].sellAmt += tx.volume * tx.price;
      }
    });

    Object.entries(brokerMap).forEach(([broker, data]) => {
      const buyAve = data.buyVol > 0 ? data.buyAmt / data.buyVol : 0;
      const sellAve = data.sellVol > 0 ? data.sellAmt / data.sellVol : 0;
      brokerRows.push({
        date, symbol, broker,
        buyVol: data.buyVol, buyAmt: Math.round(data.buyAmt * 100) / 100, buyAve: Math.round(buyAve * 10000) / 10000,
        sellVol: data.sellVol, sellAmt: Math.round(data.sellAmt * 100) / 100, sellAve: Math.round(sellAve * 10000) / 10000,
        netAmt: Math.round((data.buyAmt - data.sellAmt) * 100) / 100,
        totalValue: Math.round((data.buyAmt + data.sellAmt) * 100) / 100,
        netShares: data.buyVol - data.sellVol,
        _source: 'pdf',
      });
    });

    // Derive OHLCV from transactions
    const prices = txs.map(t => t.price).filter(p => p > 0);
    const totalVol = txs.reduce((s, t) => s + t.volume, 0);
    const totalVal = txs.reduce((s, t) => s + t.volume * t.price, 0);

    if (prices.length > 0) {
      priceRows.push({
        date, symbol,
        open: prices[prices.length - 1], // first trade (reversed time order)
        high: Math.max(...prices),
        low: Math.min(...prices),
        close: prices[0], // last trade
        volume: totalVol,
        value: Math.round(totalVal * 100) / 100,
        _source: 'pdf',
      });
    }
  });

  return { brokerRows, priceRows };
}

/**
 * Parse COL Financial Historical Broker Transactions data
 * Format: Rank | Symbol(code) | Name | Buy Vol | Buy Amt | Buy Ave | Sell Vol | Sell Amt | Sell Ave | Net Amount | Total Value
 */
export function parseColBrokerData(lines, symbol, tradingDate) {
  const records = [];

  for (const line of lines) {
    const lower = line.toLowerCase().trim();
    if (!lower) continue;
    // Skip headers
    if (lower.includes('symbol') && lower.includes('name') && lower.includes('buy vol')) continue;
    if (lower.includes('historical') || lower.includes('search by') || lower.includes('date from')) continue;

    // Split by tabs or multiple spaces
    let parts = line.split(/\t+/).map(s => s.trim()).filter(Boolean);
    if (parts.length < 8) {
      // Try splitting by 2+ spaces (for space-separated data)
      parts = line.split(/\s{2,}/).map(s => s.trim()).filter(Boolean);
    }
    if (parts.length < 8) continue;

    // Detect row format: first part might be a rank number
    let offset = 0;
    if (/^\d{1,3}$/.test(parts[0]) && parts.length >= 9) {
      offset = 1; // Skip rank number
    }

    // Check if second field (after offset) is a broker code number (2-3 digits)
    let brokerCodeOffset = 0;
    if (/^\d{2,3}$/.test(parts[offset])) {
      brokerCodeOffset = 1; // Skip broker code
    }

    const nameIdx = offset + brokerCodeOffset;
    const broker = parts[nameIdx];
    if (!broker || /^[\d.,\s-]+$/.test(broker)) continue;

    // Remaining fields should be numbers: Buy Vol, Buy Amt, Buy Ave, Sell Vol, Sell Amt, Sell Ave, Net Amount, Total Value
    const numFields = parts.slice(nameIdx + 1).map(s => parseNumber(s));
    if (numFields.length < 6) continue;

    const buyVol = numFields[0] || 0;
    const buyAmt = numFields[1] || 0;
    const buyAve = numFields[2] || 0;
    const sellVol = numFields[3] || 0;
    const sellAmt = numFields[4] || 0;
    const sellAve = numFields[5] || 0;
    const netAmt = numFields.length > 6 ? numFields[6] : (buyAmt - sellAmt);
    const totalValue = numFields.length > 7 ? numFields[7] : (buyAmt + sellAmt);

    if (buyVol === 0 && sellVol === 0) continue;

    records.push({
      date: tradingDate,
      symbol,
      broker: normalizeBrokerName(broker),
      buyVol,
      buyAmt: Math.round(buyAmt * 100) / 100,
      buyAve: Math.round(buyAve * 10000) / 10000,
      sellVol,
      sellAmt: Math.round(sellAmt * 100) / 100,
      sellAve: Math.round(sellAve * 10000) / 10000,
      netAmt: Math.round(netAmt * 100) / 100,
      totalValue: Math.round(totalValue * 100) / 100,
      netShares: buyVol - sellVol,
      _source: 'col',
    });
  }

  return records;
}

function normalizeBrokerName(name) {
  // Shorten long COL broker names to match common references
  const map = {
    'COL FINANCIAL GROUP': 'COL Financial',
    'FIRST METRO SECURITIES': 'First Metro',
    'BPI SECURITIES': 'BPI Securities',
    'ABACUS SECURITIES': 'Abacus',
    'UBS SECURITIES': 'UBS',
    'BDO SECURITIES': 'BDO Securities',
    'CLSA PHILIPPINES': 'CLSA',
    'ASIASEC EQUITIES': 'Asiasec',
    'CTS GLOBAL EQUITY': 'CTS Global',
    'SECURITIES SPECIALISTS': 'Securities Specialists',
    'AB CAPITAL SECURITIES': 'AB Capital',
    'SB EQUITIES': 'SB Equities',
    'PHILIPPINE EQUITY': 'Philippine Equity',
    'AP SECURITIES': 'AP Securities',
    'MACQUARIE CAPITAL': 'Macquarie',
    'PAPA SECURITIES': 'Papa Securities',
    'PHILSTOCKS FINANCIAL': 'Philstocks',
    'WEALTH SECURITIES': 'Wealth',
    'MAYBANK SECURITIES': 'Maybank ATR',
    'LANDBANK SECURITIES': 'Landbank',
    'UNICAPITAL SECURITIES': 'Unicapital',
    'DRAGONFI SECURITIES': 'DragonFi',
    'INVESTORS SECURITIES': 'Investors Securities',
    'F. YAP SECURITIES': 'F. Yap',
    'LUCKY SECURITIES': 'Lucky Securities',
    'RCBC SECURITIES': 'RCBC Securities',
    'MERCANTILE SECURITIES': 'Mercantile',
    'R. NUBLA SECURITIES': 'R. Nubla',
    'AAA SOUTHEAST EQUITIES': 'AAA Equities',
    'FIDELITY SECURITIES': 'Fidelity',
    'WESTLINK GLOBAL': 'Westlink',
    'QUALITY INVESTMENTS': 'Quality Investments',
    'LUYS SECURITIES': 'Luys Securities',
    'ANSALDO, GODINEZ': 'Ansaldo Godinez',
    'AURORA SECURITIES': 'Aurora',
    'MANDARIN SECURITIES': 'Mandarin',
    'REGINA CAPITAL': 'Regina Capital',
    'CITISEC': 'Citisec',
  };

  const upper = name.toUpperCase().replace(/[.,]+$/, '').trim();
  for (const [key, val] of Object.entries(map)) {
    if (upper.includes(key)) return val;
  }
  // Fallback: Title case truncation
  return name.replace(/\s*(SECURITIES|EQUITIES|CORPORAT|INCORPOR|PHILIPPI|INC\.?|GROUP|COMPANY|PARTN)\s*/gi, '').trim();
}

function parseNumber(str) {
  if (!str) return 0;
  const cleaned = str.replace(/[,\s%()]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Full PDF parse pipeline
 */
export async function parsePdfFile(file) {
  const lines = await extractPdfText(file);
  const reportType = detectReportType(lines);
  const detectedSymbol = detectSymbol(lines);
  const detectedDate = detectDate(lines);

  return { lines, reportType, detectedSymbol, detectedDate, rawText: lines.join('\n') };
}
